import { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "../../common/components/LocalizationProvider";

interface UserOption {
  id: number;
  name?: string;
  email?: string;
}

interface UserNameFilterProps {
  onChange: (userId: string) => void;
  users: UserOption[];
  value?: string;
  allowAllOption?: boolean;
  disabled?: boolean;
}

const EMPTY_USERS_MAP = Object.freeze<Record<number, string | undefined>>({});

const FORM_CONTROL_SX = {
  minWidth: 240,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset": { borderColor: "divider" },
    "&:hover fieldset": { borderColor: "primary.main" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};

const SELECT_SX = { backgroundColor: "background.paper" };

const UserNameFilter = ({
  onChange,
  users,
  value = "",
  allowAllOption = true,
  disabled = false,
}: UserNameFilterProps) => {
  const t = useTranslation();
  const usersMap = useSelector((state) => state.usersMap ?? EMPTY_USERS_MAP, shallowEqual);
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setSelectedValue(value ?? "");
  }, [value]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const nextValue = event.target.value ?? "";
    setSelectedValue(nextValue);
    onChange(nextValue);
  };

  const getDisplayName = (user: UserOption) => {
    const fromMap = usersMap[user.id];
    if (fromMap) {
      return fromMap;
    }
    if (user.name) {
      return user.name;
    }
    if (user.email) {
      return user.email;
    }
    return `${user.id}`;
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" sx={FORM_CONTROL_SX} disabled={disabled}>
      <InputLabel id="user-select-label">{t("shared.user")}</InputLabel>
      <Select
        labelId="user-select-label"
        value={selectedValue}
        onChange={handleChange}
        label={t("shared.user")}
        displayEmpty
        size="small"
        sx={SELECT_SX}
      >
        {allowAllOption && (
          <MenuItem value="">
            <em>Todos os usuários</em>
          </MenuItem>
        )}
        {users.map((user) => (
          <MenuItem key={user.id} value={user.id.toString()}>
            {getDisplayName(user)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default UserNameFilter;
